import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Bot, User } from "lucide-react";
import { chatStreamUrl } from "../lib/api";
import { CONTACT } from "../data/content";

// Bottom-right floating widgets: WhatsApp + AI Chat (streaming SSE)
export default function FloatingWidgets() {
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(() => (window.crypto?.randomUUID?.() || `sid-${Date.now()}`));
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hello! I'm TechmateAI. Ask me anything about cybersecurity, compliance (ISO 27001, DPDP, SOC 2) or how to start your Cyber Risk Navigator." },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streaming]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(chatStreamUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      if (!res.body) throw new Error("No response body");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          try {
            const obj = JSON.parse(payload);
            if (obj.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + obj.delta };
                return copy;
              });
            } else if (obj.error) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: "Sorry, something went wrong. Please try again or book a consultation." };
                return copy;
              });
            }
          } catch { /* ignore */ }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, I couldn't reach our servers. Please book a consultation and our team will respond." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      {/* WhatsApp */}
      <a
        href={`https://wa.me/${CONTACT.whatsapp.replace(/[^0-9]/g, "")}`}
        target="_blank"
        rel="noreferrer"
        data-testid="floating-whatsapp-btn"
        className="print-hide fixed bottom-24 right-4 sm:right-6 z-40 h-12 w-12 sm:h-14 sm:w-14 rounded-full bg-[#25D366] hover:bg-[#20b95a] shadow-xl grid place-items-center text-white transition-transform hover:scale-105"
        aria-label="Chat on WhatsApp"
      >
        <svg viewBox="0 0 32 32" className="h-6 w-6 sm:h-7 sm:w-7" fill="currentColor" aria-hidden>
          <path d="M19.11 17.5c-.28-.14-1.66-.82-1.92-.91-.26-.1-.45-.14-.63.14-.19.28-.72.9-.88 1.08-.16.19-.32.21-.6.07-.28-.14-1.18-.43-2.25-1.38-.83-.74-1.39-1.65-1.55-1.93-.16-.28-.02-.43.12-.57.13-.13.28-.32.42-.48.14-.16.19-.28.28-.47.09-.19.05-.35-.02-.49-.07-.14-.63-1.53-.86-2.09-.23-.55-.46-.48-.63-.49h-.54c-.19 0-.49.07-.75.35-.26.28-.98.96-.98 2.34 0 1.38 1 2.71 1.14 2.9.14.19 1.98 3.03 4.8 4.13.67.29 1.19.47 1.6.6.67.21 1.28.18 1.76.11.54-.08 1.66-.68 1.89-1.34.23-.66.23-1.22.16-1.34-.07-.13-.26-.19-.54-.32ZM16.02 5.33c-5.9 0-10.7 4.8-10.7 10.7 0 1.9.5 3.75 1.44 5.38L4.5 26.67l5.4-1.4a10.71 10.71 0 0 0 6.12 1.9h.01c5.9 0 10.7-4.8 10.7-10.7 0-2.86-1.11-5.54-3.13-7.56a10.63 10.63 0 0 0-7.58-3.14Zm0 19.4h-.01a8.9 8.9 0 0 1-4.54-1.24l-.32-.19-3.2.83.86-3.12-.21-.32a8.86 8.86 0 1 1 16.32-4.87c0 4.9-4 8.91-8.9 8.91Z" />
        </svg>
      </a>

      {/* AI Chat Toggle */}
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="ai-chat-toggle-btn"
        className="print-hide fixed bottom-6 right-4 sm:right-6 z-40 h-14 w-14 sm:h-16 sm:w-16 rounded-full bg-primary hover:bg-primary/90 text-white shadow-2xl grid place-items-center transition-transform hover:scale-105"
        aria-label="Open AI chat"
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6 sm:h-7 sm:w-7" />}
      </button>

      {/* Chat Panel */}
      {open && (
        <div
          data-testid="ai-chat-panel"
          className="print-hide fixed bottom-24 right-4 sm:right-6 z-40 w-[calc(100vw-2rem)] sm:w-[400px] h-[540px] max-h-[80vh] rounded-3xl border border-border bg-card shadow-2xl overflow-hidden flex flex-col"
        >
          <div className="px-4 py-3 bg-gradient-to-r from-primary to-[#0a3560] text-white flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-white/15 grid place-items-center"><Bot className="h-5 w-5" /></div>
            <div>
              <div className="font-display font-semibold text-sm">TechmateAI Assistant</div>
              <div className="text-[10px] text-white/70">Claude Sonnet 4.5 • Cybersecurity</div>
            </div>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 no-scrollbar">
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                {m.role === "assistant" && (
                  <div className="h-7 w-7 rounded-lg bg-primary/10 text-primary grid place-items-center shrink-0"><Bot className="h-4 w-4" /></div>
                )}
                <div
                  data-testid={`chat-msg-${m.role}-${i}`}
                  className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-primary text-primary-foreground rounded-br-sm"
                      : "bg-muted text-foreground rounded-bl-sm"
                  }`}
                >
                  {m.content || (streaming && i === messages.length - 1 ? "…" : "")}
                </div>
                {m.role === "user" && (
                  <div className="h-7 w-7 rounded-lg bg-secondary/10 text-secondary grid place-items-center shrink-0"><User className="h-4 w-4" /></div>
                )}
              </div>
            ))}
          </div>
          <div className="p-3 border-t border-border">
            <div className="flex gap-2 items-center rounded-full border border-border bg-background pl-4 pr-1 py-1">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") send(); }}
                placeholder="Ask about ISO 27001, DPDP, cloud security…"
                data-testid="ai-chat-input"
                className="flex-1 bg-transparent outline-none text-sm py-2"
                disabled={streaming}
              />
              <button
                onClick={send}
                disabled={streaming || !input.trim()}
                data-testid="ai-chat-send-btn"
                className="h-9 w-9 rounded-full bg-primary hover:bg-primary/90 disabled:opacity-50 text-white grid place-items-center transition-colors"
                aria-label="Send"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
